import Pusher from 'pusher-js';

const REVERB_KEY = import.meta.env.VITE_REVERB_KEY;
const REVERB_HOST = import.meta.env.VITE_REVERB_HOST;
const REVERB_PORT = 443;
const FORCE_TLS = true;

let pusherInstance: Pusher | null = null;

export interface DriverLocation {
  longitude: number;
  latitude: number;
  timestamp?: number;
  nextStopBarcodes?: string[];
}

const normalizeBarcode = (value: string) => value.trim().toUpperCase();

const getNextStopBarcodes = (data: Record<string, unknown>): string[] => {
  const raw = data.next_stop_barcodes;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter(
    (barcode): barcode is string =>
      typeof barcode === 'string' && barcode.trim().length > 0
  );
};

const hasVisibleDriverStop = (
  data: Record<string, unknown>,
  trackingBarcode?: string | null
) => {
  const barcodes = getNextStopBarcodes(data);

  if (barcodes.length === 0 || !trackingBarcode?.trim()) {
    return false;
  }

  const normalizedTracking = normalizeBarcode(trackingBarcode);

  return barcodes.some(
    (barcode) => normalizeBarcode(barcode) === normalizedTracking
  );
};

const getChannelName = (driverId: string) => `driver-location-${driverId}`;

const parseLocationData = (
  data: Record<string, unknown>
): DriverLocation | null => {
  const longitude = (data.longitude ?? data.lng ?? data.lon) as number;
  const latitude = (data.latitude ?? data.lat) as number;
  const timestamp = (data.timestamp ?? data.time ?? Date.now()) as number;

  if (
    typeof longitude === 'number' &&
    typeof latitude === 'number' &&
    !isNaN(longitude) &&
    !isNaN(latitude) &&
    longitude !== 0 &&
    latitude !== 0
  ) {
    return { longitude, latitude, timestamp };
  }

  return null;
};

const getPusherInstance = (): Pusher | null => {
  if (typeof window === 'undefined') return null;

  if (!REVERB_KEY || !REVERB_HOST) {
    console.error('WebSocket configuration missing:', {
      REVERB_KEY: REVERB_KEY ? '✓' : '✗',
      REVERB_HOST: REVERB_HOST ? '✓' : '✗',
    });
    return null;
  }

  if (!pusherInstance) {
    pusherInstance = new Pusher(REVERB_KEY, {
      wsHost: REVERB_HOST,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: FORCE_TLS,
      cluster: 'mt1',
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
    });

    pusherInstance.connection.bind(
      'state_change',
      (state: { current: string }) => {
        console.log('WS state:', state);
      }
    );

    pusherInstance.connection.bind('error', (error: unknown) => {
      console.error('WS error:', error);
    });
  }

  return pusherInstance;
};

export const disconnectPusher = () => {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
};

export const subscribeToDriverLocation = (
  driverId: string,
  trackingBarcode: string | null | undefined,
  onLocationUpdate: (location: DriverLocation | null) => void,
  onError?: (error: unknown) => void
): (() => void) => {
  const pusher = getPusherInstance();
  if (!pusher) {
    onError?.(
      'WebSocket unavailable — check VITE_REVERB_KEY and VITE_REVERB_HOST'
    );
    return () => {};
  }

  const channelName = getChannelName(driverId);
  console.log('Subscribing to public channel:', channelName);

  const channel = pusher.subscribe(channelName);

  channel.bind('pusher:subscription_succeeded', () => {
    console.log('Subscribed!', channelName);
  });

  channel.bind('pusher:subscription_error', (error: unknown) => {
    console.error('Subscription error:', channelName, error);
    onError?.(error);
  });

  const handleLocationUpdate = (
    event: string,
    data: Record<string, unknown>
  ) => {
    if (event.startsWith('pusher:')) return;

    console.log('Event received:', event, data);

    if (!hasVisibleDriverStop(data, trackingBarcode)) {
      onLocationUpdate(null);
      return;
    }

    const location = parseLocationData(data);
    if (location) {
      onLocationUpdate({
        ...location,
        nextStopBarcodes: getNextStopBarcodes(data),
      });
    }
  };

  channel.bind_global(handleLocationUpdate);

  return () => {
    channel.unbind_global(handleLocationUpdate);
    pusher.unsubscribe(channelName);
  };
};
